import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbMenuContext } from "abreact/menu/AbMenu";
import { AbButton } from "abreact/misc/AbButton";
import * as AbHooks from "abreact/utils/AbHooks";
import * as React from "react";
export const AbPopupButton = props => {
  const [popupShown, setPopupShown] = React.useState(false);
  const uniquePopupId = AbHooks.useUniqueId();
  const popupId = props.popupProps?.id || uniquePopupId;
  const menuContext = React.useContext(AbMenuContext);
  const inMenuButtonPopup = menuContext.path !== undefined || menuContext.path !== undefined;
  const safeSetTimeout = AbHooks.useSafeTimeout();
  const prevTimeout = React.useRef(undefined);
  const partialRef = React.useRef({
    close: () => setPopupShown(false),
    open: () => setPopupShown(true),
    focus: () => partialRef.current?.buttonElement?.focus()
  });
  partialRef.current.isOpen = popupShown;
  const onMouseEnter = () => {
    if (prevTimeout.current !== undefined) return;
    if (!inMenuButtonPopup) return;
    prevTimeout.current = safeSetTimeout(() => {
      if (!partialRef.current || partialRef.current.isOpen) return;
      // Used to make sure correct menus are closed before showing this one
      partialRef.current.buttonElement?.dispatchEvent(new Event("mousedown", {
        bubbles: true
      }));
      partialRef.current.open?.();
      prevTimeout.current = undefined;
    }, MENU_BUTTON_CONFIG.hoverTimeoutMs);
  };
  const onMouseLeave = () => {
    clearTimeout(prevTimeout.current);
    prevTimeout.current = undefined;
  };
  const isWhole = partial => !!partial.buttonElement;
  const buttonRef = AbHooks.useComposeRef(props, partialRef.current, "buttonElement", isWhole);
  AbHooks.useOnValueChange(() => {
    props.onChange?.(popupShown);
  }, popupShown);
  const {
    popupContents,
    popupProps,
    onClick,
    __htmlProps,
    tooltip,
    eventHandlers,
    ...rest
  } = props;
  const onBackgroundCaptureMouseDown = e => {
    if (partialRef.current.buttonElement && !partialRef.current.buttonElement.contains(e.target)) {
      setPopupShown(false);
      return true;
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AbButton, _extends({}, rest, {
    eventHandlers: {
      ...eventHandlers,
      onMouseLeave: e => {
        eventHandlers?.onMouseLeave?.(e);
        onMouseLeave();
      },
      onMouseEnter: e => {
        eventHandlers?.onMouseEnter?.(e);
        onMouseEnter();
      }
    },
    tooltip: popupShown ? undefined : tooltip,
    className: ReactUtils.classNamesString([props.className, "ab-popup-button"]),
    onClick: e => {
      onClick?.(e);
      if (e.defaultPrevented) return;
      setPopupShown(v => !v);
    },
    abRef: buttonRef,
    __htmlProps: {
      ...__htmlProps,
      "aria-expanded": popupShown,
      "aria-owns": popupShown ? popupId : undefined,
      "aria-controls": popupShown ? popupId : undefined
    }
  })), popupShown && popupContents && /*#__PURE__*/React.createElement(AbPopup, _extends({
    origin: partialRef.current.buttonElement,
    positioner: PopupUtils.VERTICAL_POSITIONER,
    overlay: false,
    flushContent: true,
    focus: true,
    onBackgroundCaptureMouseDown: onBackgroundCaptureMouseDown,
    closeOnTabOut: true
  }, popupProps, {
    id: popupId,
    onClose: () => {
      popupProps?.onClose?.();
      setPopupShown(false);
      // Needs to be in animation frame-- should run after popup is closed
      // and focus is done moving.
      window.requestAnimationFrame(() => {
        const activeClassList = document.activeElement?.classList;
        if (activeClassList?.contains("ab-product") || activeClassList?.contains("ab-popup")) {
          partialRef.current.focus?.();
        }
      });
    }
  }), popupContents));
};
const MENU_BUTTON_CONFIG = {
  hoverTimeoutMs: 250
};
export const __TESTING_ONLY_SET_TIMEOUT = value => {
  MENU_BUTTON_CONFIG.hoverTimeoutMs = value;
};
//# sourceMappingURL=AbPopupButton.js.map
